import React, { useEffect, useMemo, useState } from 'react'
import {
  CAlert,
  CButton,
  CButtonGroup,
  CForm,
  CFormInput,
  CFormLabel,
  CInputGroup,
  CInputGroupText,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import Empty from '../../shared/Empty'
import SkeletonList from '../../shared/SkeletonList'
import { icon } from '../../shared/icons'
import { useUI } from '../../shared/ui/UIProvider'
import { request } from '../../services'
import { envConfig, preferredEnv } from '../../services/config'

const emptyForm = { global_parameter: '', value: '' }

function GlobalParametersPage({ onError }) {
  const { toast, confirm } = useUI()
  const envName = preferredEnv()
  const env = envConfig()
  const [parameters, setParameters] = useState([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(emptyForm)
  const [message, setMessage] = useState('')

  async function loadParameters() {
    setLoading(true)
    const res = await request('global_parameters')
    setLoading(false)
    if (!res.ok) {
      onError(res.data?.error || 'No se pudieron cargar los parametros globales')
      return
    }
    setParameters(Array.isArray(res.data) ? res.data : Array.isArray(res.data?.data) ? res.data.data : [])
  }

  useEffect(() => {
    loadParameters()
  }, [])

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return parameters
    return parameters.filter((parameter) => {
      return [parameter.global_parameter, parameter.value].join(' ').toLowerCase().includes(term)
    })
  }, [parameters, search])

  function openCreate() {
    setEditing(null)
    setForm(emptyForm)
    setMessage('')
    setModalOpen(true)
  }

  function openEdit(parameter) {
    setEditing(parameter)
    setForm({
      global_parameter: parameter.global_parameter || '',
      value: parameter.value ?? '',
    })
    setMessage('')
    setModalOpen(true)
  }

  async function submit(event) {
    event.preventDefault()
    if (!form.global_parameter.trim() || form.value === '') {
      setMessage('Captura el nombre del parametro y su valor')
      return
    }
    const originalGlobalParameter = String(editing?.global_parameter || '').trim()
    const payload = {
      global_parameter: form.global_parameter.trim(),
      value: String(form.value),
    }
    if (editing && originalGlobalParameter && originalGlobalParameter !== payload.global_parameter) {
      payload.old_global_parameter = originalGlobalParameter
    }
    console.log(editing ? '[global parameters] update payload' : '[global parameters] create payload', payload)
    setSaving(true)
    const res = await request(editing ? 'global_parameter_update' : 'global_parameter_create', {
      method: 'POST',
      body: payload,
    })
    console.log(editing ? '[global parameters] update response' : '[global parameters] response', res)
    setSaving(false)
    if (!res.ok) {
      setMessage(res.data?.error || res.data?.message || 'No se pudo guardar el parametro')
      return
    }
    setModalOpen(false)
    toast(editing ? `Parametro "${form.global_parameter}" actualizado` : `Parametro "${form.global_parameter}" creado`, 'success')
    await loadParameters()
  }

  async function remove(parameter) {
    const confirmed = await confirm({
      title: 'Eliminar parametro global',
      body: `Vas a eliminar "${parameter.global_parameter}". Esta accion no se puede deshacer.`,
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      tone: 'danger',
      icon: 'cilTrash',
    })
    if (!confirmed) return
    const globalParameter = parameter.global_parameter || parameter.globalParameter
    const payload = { global_parameter: globalParameter }
    console.log('[global parameters] delete item', parameter)
    console.log('[global parameters] delete global_parameter', globalParameter)
    console.log('[global parameters] delete payload', payload)
    const res = await request('global_parameter_delete', { method: 'POST', body: payload })
    console.log('[global parameters] delete response', res)
    if (!res.ok) {
      const detail = res.data?.error || res.data?.message || 'No se pudo eliminar el parametro'
      onError(detail)
      toast(detail, 'danger')
      return
    }
    toast(`Parametro "${parameter.global_parameter}" eliminado`, 'success')
    await loadParameters()
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Parametros Globales</h1>
          <p>Altas, bajas y cambios de configuracion global del sistema. Ambiente {envName}: {env.baseURL}</p>
        </div>
        <CButtonGroup>
          <CButton color="light" onClick={loadParameters}><CIcon icon={icon('cilReload')} /> Recargar</CButton>
          <CButton color="success" className="btn-brand" onClick={openCreate}>
            <CIcon icon={icon('cilPlus')} /> Crear parametro
          </CButton>
        </CButtonGroup>
      </div>

      <CInputGroup className="search-wrap">
        <CInputGroupText><CIcon icon={icon('cilMagnifyingGlass')} /></CInputGroupText>
        <CFormInput placeholder="Buscar parametro" value={search} onChange={(event) => setSearch(event.target.value)} />
      </CInputGroup>

      {loading ? <SkeletonList /> : filtered.map((parameter) => (
        <div className="card-x parameter-row" key={parameter.global_parameter}>
          <div className="row-top">
            <div>
              <h3>{parameter.global_parameter}</h3>
              <p className="parameter-value">{parameter.value}</p>
            </div>
            <div className="parameter-actions">
              <CButton color="light" size="sm" onClick={() => openEdit(parameter)}>
                <CIcon icon={icon('cilPencil')} /> Editar
              </CButton>
              <CButton color="light" size="sm" onClick={() => remove(parameter)}>
                <CIcon icon={icon('cilTrash')} /> Eliminar
              </CButton>
            </div>
          </div>
        </div>
      ))}

      {!loading && filtered.length === 0 ? <Empty icon={icon('cilSettings')} text="No hay parametros globales para mostrar." /> : null}

      <CModal visible={modalOpen} onClose={() => setModalOpen(false)}>
        <CForm onSubmit={submit}>
          <CModalHeader>
            <CModalTitle>{editing ? 'Editar parametro global' : 'Crear parametro global'}</CModalTitle>
          </CModalHeader>
          <CModalBody>
            {message ? <CAlert color="warning">{message}</CAlert> : null}
            <div className="mb-3">
              <CFormLabel>Nombre del parametro</CFormLabel>
              <CFormInput
                value={form.global_parameter}
                placeholder="MAX_RETRIES"
                onChange={(event) => setForm({
                  ...form,
                  global_parameter: event.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ''),
                })}
                required
              />
            </div>
            <div>
              <CFormLabel>Valor</CFormLabel>
              <CFormInput value={form.value} onChange={(event) => setForm({ ...form, value: event.target.value })} required />
            </div>
          </CModalBody>
          <CModalFooter>
            <CButton color="light" onClick={() => setModalOpen(false)} disabled={saving}>Cancelar</CButton>
            <CButton color="success" className="btn-brand" type="submit" disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar'}
            </CButton>
          </CModalFooter>
        </CForm>
      </CModal>
    </>
  )
}

export default GlobalParametersPage
